import { Info } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type { ReportCaveat } from '@/features/reports/api/use-sales-report-query'
import { formatDate } from '@/features/reports/lib/format'

const KNOWN_CODES = new Set([
  'OPERATOR_DATA_STARTS',
  'CATEGORY_REPARENTING_IS_RETROACTIVE',
])

/**
 * What the data cannot say (CHG-2026-09-manager-reporting).
 *
 * These reach the reader on purpose. An empty channel chart for August is not a month with no
 * self-checkout revenue — it is a month before the operator was recorded at all, and rendering it
 * as a zero result would be the most confident wrong number on the page.
 *
 * The server sends codes and the wording lives here, so the notes are translated and cannot drift
 * between German and English. A code this build does not know renders as nothing: silence is the
 * right failure mode for an advisory note, and a raw constant on screen would be worse than none.
 */
export function CaveatNotes({ caveats }: { caveats: Array<ReportCaveat> }) {
  const { t } = useTranslation('reports')
  const known = caveats.filter((c) => KNOWN_CODES.has(c.code))
  if (known.length === 0) return null

  return (
    <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs leading-5 text-muted-foreground">
      <ul className="space-y-1.5">
        {known.map((caveat) => (
          <li key={caveat.code} className="flex gap-2">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              {t(`caveats.${caveat.code}`, {
                date: caveat.since ? formatDate(caveat.since) : '',
              })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
